import React from "react";
import {
  PlusCircle,
  X,
  Upload,
  Link,
  ChevronRight,
  Plus,
  CheckCircle2,
  Image as ImageIcon
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import ImageThumbnail from "@/components/common/ImageThumbnail";
import AutocompleteInput from "@/components/common/AutocompleteInput";
import { formatNumber, formatCurrency } from "@/utils/formatters";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * AddQuoteModal - Modal for adding a quote entry to an existing type
 */
export const AddQuoteModal = ({
  isOpen,
  onClose,
  addQuoteType,
  newQuote,
  setNewQuote,
  onSubmit
}) => {
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Agregar Cotización</DialogTitle>
          <DialogDescription>
            {addQuoteType && (
              <span className="flex items-center gap-2 mt-1">
                <strong>{addQuoteType.name}</strong>
                <Badge variant={addQuoteType.material === "Aluminio" ? "default" : "secondary"}>
                  {addQuoteType.material}
                </Badge>
                {addQuoteType.series && <Badge variant="outline">{addQuoteType.series}</Badge>}
              </span>
            )}
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="quote-width">Ancho (m) *</Label>
              <Input
                id="quote-width"
                data-testid="add-quote-width"
                type="number"
                step="0.01"
                min="0"
                placeholder="Ej: 1.20"
                value={newQuote.width}
                onChange={(e) => setNewQuote({...newQuote, width: e.target.value})}
              />
            </div>
            <div>
              <Label htmlFor="quote-height">Alto (m) *</Label>
              <Input
                id="quote-height"
                data-testid="add-quote-height"
                type="number"
                step="0.01"
                min="0"
                placeholder="Ej: 2.10"
                value={newQuote.height}
                onChange={(e) => setNewQuote({...newQuote, height: e.target.value})}
              />
            </div>
          </div>
          
          <div>
            <Label htmlFor="quote-cost">Costo Total *</Label>
            <Input
              id="quote-cost"
              data-testid="add-quote-cost"
              type="number"
              step="0.01"
              min="0"
              placeholder="Ej: 8500"
              value={newQuote.cost}
              onChange={(e) => setNewQuote({...newQuote, cost: e.target.value})}
            />
          </div>
          
          <div>
            <Label htmlFor="quote-notes">Notas (opcional)</Label>
            <Input
              id="quote-notes"
              data-testid="add-quote-notes"
              placeholder="Ej: Cotización cliente X"
              value={newQuote.notes}
              onChange={(e) => setNewQuote({...newQuote, notes: e.target.value})}
            />
          </div>
          
          {newQuote.width && newQuote.height && newQuote.cost && (
            <div className="bg-slate-100 rounded-sm p-3">
              <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">Vista previa</p>
              <p className="font-mono text-sm">
                Área: {formatNumber(parseFloat(newQuote.width) * parseFloat(newQuote.height), 4)} m² • 
                Costo/m²: {formatCurrency(parseFloat(newQuote.cost) / (parseFloat(newQuote.width) * parseFloat(newQuote.height)))}
              </p>
            </div>
          )}
        </div>
        
        <DialogFooter>
          <Button variant="secondary" onClick={() => onClose(false)}>
            Cancelar
          </Button>
          <Button onClick={onSubmit} data-testid="add-quote-submit">
            <PlusCircle className="w-4 h-4 mr-2" />
            Agregar Cotización
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

/**
 * ManualAddModal - Modal for manually adding a new quote type
 */
export const ManualAddModal = ({
  isOpen,
  onClose,
  manualAddData,
  setManualAddData,
  onSubmit,
  onImageUpload,
  isSubmitting,
  materials,
  aluminumSeries,
  allTypeNames
}) => {
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <PlusCircle className="w-5 h-5" />
            Añadir Tipo Manualmente
          </DialogTitle>
          <DialogDescription>
            Crea un nuevo tipo de cotización manualmente cuando el archivo no es reconocido
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4">
          {/* Image Upload with Drag & Drop and Paste Support */}
          <div className="space-y-2">
            <Label>Imagen (opcional)</Label>
            <div className="flex items-center gap-4">
              <div 
                className={`w-24 h-24 border-2 border-dashed rounded-sm flex items-center justify-center bg-slate-50 overflow-hidden cursor-pointer transition-colors ${
                  manualAddData.image_data ? 'border-emerald-400' : 'border-slate-300 hover:border-slate-400'
                }`}
                onClick={() => document.getElementById('manual-image-input').click()}
                onDragOver={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  e.currentTarget.classList.add('border-blue-500', 'bg-blue-50');
                }}
                onDragLeave={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  e.currentTarget.classList.remove('border-blue-500', 'bg-blue-50');
                }}
                onDrop={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  e.currentTarget.classList.remove('border-blue-500', 'bg-blue-50');
                  const files = e.dataTransfer.files;
                  if (files && files[0] && files[0].type.startsWith('image/')) {
                    const reader = new FileReader();
                    reader.onloadend = () => {
                      setManualAddData(prev => ({ ...prev, image_data: reader.result }));
                    };
                    reader.readAsDataURL(files[0]);
                  }
                }}
                onPaste={(e) => {
                  const items = e.clipboardData?.items;
                  if (items) {
                    for (let i = 0; i < items.length; i++) {
                      if (items[i].type.indexOf('image') !== -1) {
                        const blob = items[i].getAsFile();
                        const reader = new FileReader();
                        reader.onloadend = () => {
                          setManualAddData(prev => ({ ...prev, image_data: reader.result }));
                        };
                        reader.readAsDataURL(blob);
                        break;
                      }
                    }
                  }
                }}
                tabIndex={0}
              >
                {manualAddData.image_data ? (
                  <img 
                    src={manualAddData.image_data} 
                    alt="Preview" 
                    className="w-full h-full object-contain"
                  />
                ) : (
                  <ImageIcon className="w-8 h-8 text-slate-300" />
                )}
              </div>
              <div className="flex-1 space-y-2">
                <input
                  id="manual-image-input"
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={onImageUpload}
                  data-testid="manual-image-input"
                />
                <div className="flex items-center gap-2">
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => document.getElementById('manual-image-input').click()}
                  >
                    <Upload className="w-4 h-4 mr-1" />
                    Subir imagen
                  </Button>
                  {manualAddData.image_data && (
                    <Button 
                      variant="ghost" 
                      size="sm"
                      className="text-red-600"
                      onClick={() => setManualAddData(prev => ({ ...prev, image_data: null }))}
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  )}
                </div>
                <p className="text-xs text-slate-500">
                  Arrastra una imagen, o pega con Ctrl+V
                </p>
              </div>
            </div>
          </div>

          {/* Name/Code with Autocomplete */}
          <div className="space-y-2">
            <Label>Código / Nombre del Tipo *</Label>
            <AutocompleteInput
              data-testid="manual-name-input"
              placeholder="Ej: MV.01, Ventana Corrediza, etc."
              value={manualAddData.name}
              onChange={(e) => setManualAddData(prev => ({ ...prev, name: e.target.value }))}
              suggestions={allTypeNames}
              maxSuggestions={15}
              minCharsToShow={1}
            />
            <p className="text-xs text-slate-500">Escribe para buscar tipos existentes o crear uno nuevo</p>
          </div>

          {/* Material and Series */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Material *</Label>
              <Select 
                value={manualAddData.material} 
                onValueChange={(value) => setManualAddData(prev => ({ 
                  ...prev, 
                  material: value,
                  series: value !== "Aluminio" ? "" : prev.series 
                }))}
              >
                <SelectTrigger data-testid="manual-material-select">
                  <SelectValue placeholder="Seleccionar..." />
                </SelectTrigger>
                <SelectContent>
                  {materials.map(mat => (
                    <SelectItem key={mat} value={mat}>{mat}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Serie {manualAddData.material === "Aluminio" ? "" : "(solo Aluminio)"}</Label>
              <Select 
                value={manualAddData.series || "none"} 
                onValueChange={(value) => setManualAddData(prev => ({ ...prev, series: value === "none" ? "" : value }))}
                disabled={manualAddData.material !== "Aluminio"}
              >
                <SelectTrigger data-testid="manual-series-select">
                  <SelectValue placeholder="Opcional..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Sin serie</SelectItem>
                  {aluminumSeries.map(ser => (
                    <SelectItem key={ser} value={ser}>{ser}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Color */}
          <div className="space-y-2">
            <Label>Color</Label>
            <Select 
              value={manualAddData.color || "none"} 
              onValueChange={(value) => setManualAddData(prev => ({ ...prev, color: value === "none" ? "" : value }))}
            >
              <SelectTrigger data-testid="manual-color-select">
                <SelectValue placeholder="Opcional..." />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Sin especificar</SelectItem>
                <SelectItem value="Blanco">Blanco</SelectItem>
                <SelectItem value="Negro">Negro</SelectItem>
                <SelectItem value="Gris">Gris</SelectItem>
                <SelectItem value="Natural">Natural</SelectItem>
                <SelectItem value="Anodizado">Anodizado</SelectItem>
                <SelectItem value="Pintura Electrostática">Pintura Electrostática</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Glass Spec and Extra Specs (Varios) */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Vidrio</Label>
              <Input
                data-testid="manual-glass-input"
                placeholder="Ej: 6mm Templado, DVH 4+12+4"
                value={manualAddData.glass_spec || ""}
                onChange={(e) => setManualAddData(prev => ({ ...prev, glass_spec: e.target.value }))}
              />
            </div>
            <div className="space-y-2">
              <Label>Varios (características)</Label>
              <Input
                data-testid="manual-extras-input"
                placeholder="Ej: Con mosquitero, Doble hoja"
                value={manualAddData.extra_specs || ""}
                onChange={(e) => setManualAddData(prev => ({ ...prev, extra_specs: e.target.value }))}
              />
            </div>
          </div>

          {/* Dimensions and Cost */}
          <div className="grid grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label>Ancho (m) *</Label>
              <Input
                data-testid="manual-width-input"
                type="number"
                step="0.01"
                placeholder="1.20"
                value={manualAddData.width_m}
                onChange={(e) => setManualAddData(prev => ({ ...prev, width_m: e.target.value }))}
              />
            </div>
            <div className="space-y-2">
              <Label>Alto (m) *</Label>
              <Input
                data-testid="manual-height-input"
                type="number"
                step="0.01"
                placeholder="1.50"
                value={manualAddData.height_m}
                onChange={(e) => setManualAddData(prev => ({ ...prev, height_m: e.target.value }))}
              />
            </div>
            <div className="space-y-2">
              <Label>Costo (USD) *</Label>
              <Input
                data-testid="manual-cost-input"
                type="number"
                step="0.01"
                placeholder="5000"
                value={manualAddData.cost}
                onChange={(e) => setManualAddData(prev => ({ ...prev, cost: e.target.value }))}
              />
            </div>
          </div>

          {/* Preview calculation */}
          {manualAddData.width_m && manualAddData.height_m && manualAddData.cost && (
            <div className="bg-slate-50 rounded-sm p-3 border">
              <div className="flex justify-between text-sm">
                <span className="text-slate-500">Área:</span>
                <span className="font-mono">{formatNumber(parseFloat(manualAddData.width_m) * parseFloat(manualAddData.height_m))} m²</span>
              </div>
              <div className="flex justify-between text-sm mt-1">
                <span className="text-slate-500">Costo por m²:</span>
                <span className="font-mono font-medium text-emerald-600">
                  {formatCurrency(parseFloat(manualAddData.cost) / (parseFloat(manualAddData.width_m) * parseFloat(manualAddData.height_m)))}
                </span>
              </div>
            </div>
          )}
        </div>

        <DialogFooter className="mt-4">
          <Button variant="outline" onClick={() => onClose(false)}>
            Cancelar
          </Button>
          <Button 
            onClick={onSubmit}
            disabled={isSubmitting}
            data-testid="manual-add-submit-btn"
          >
            {isSubmitting ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                Guardando...
              </>
            ) : (
              <>
                <CheckCircle2 className="w-4 h-4 mr-2" />
                Guardar Tipo
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
